import _ from 'lodash';

// Week order, 0 = Sunday ... 6 = Saturday (matches JS Date#getDay and the backend rateWindows.days)
export const WEEK_DAYS = [
  { label: 'Sunday', short: 'Sun', value: 0 },
  { label: 'Monday', short: 'Mon', value: 1 },
  { label: 'Tuesday', short: 'Tue', value: 2 },
  { label: 'Wednesday', short: 'Wed', value: 3 },
  { label: 'Thursday', short: 'Thu', value: 4 },
  { label: 'Friday', short: 'Fri', value: 5 },
  { label: 'Saturday', short: 'Sat', value: 6 },
];

export const ALL_DAYS = _.map(WEEK_DAYS, 'value');

export const RATE_WINDOW_TYPES = [
  { label: 'Peak', value: 'peak' },
  { label: 'Off-Peak', value: 'off_peak' },
  { label: 'Other', value: 'other' },
];

export const HH_MM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export const rateWindowTypeLabel = (type) => _.get(_.find(RATE_WINDOW_TYPES, { value: type }), 'label', type || '');

// "Sun, Mon, Tue" in week order; "All days" when every day is selected
export const daysLabel = (days) => {
  const selected = _.uniq(_.map(days, Number));
  if (_.isEmpty(selected)) return '';
  if (ALL_DAYS.every((d) => selected.includes(d))) return 'All days';
  return _.map(
    _.filter(WEEK_DAYS, (d) => selected.includes(d.value)),
    'short'
  ).join(', ');
};

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

// Expand a window into absolute minute ranges within a week (0 .. 7*1440).
// A window whose `to` <= `from` runs past midnight into the next day.
const weeklyRanges = (rateWindow) => {
  const from = toMinutes(rateWindow.from);
  let to = toMinutes(rateWindow.to);
  if (to <= from) to += 1440;
  const ranges = [];
  _.forEach(_.uniq(_.map(rateWindow.days, Number)), (day) => {
    const start = day * 1440 + from;
    const end = day * 1440 + to;
    const weekEnd = 7 * 1440;
    if (end > weekEnd) {
      // Saturday night spilling into Sunday wraps to the start of the week
      ranges.push([start, weekEnd]);
      ranges.push([0, end - weekEnd]);
    } else {
      ranges.push([start, end]);
    }
  });
  return ranges;
};

// Returns a message describing the first overlapping pair of windows, or null.
export const findRateWindowOverlap = (windows) => {
  const valid = _.filter(
    windows,
    (rw) => rw && HH_MM_REGEX.test(rw.from || '') && HH_MM_REGEX.test(rw.to || '') && rw.from !== rw.to && !_.isEmpty(rw.days)
  );
  for (let i = 0; i < valid.length; i++) {
    const a = weeklyRanges(valid[i]);
    for (let j = i + 1; j < valid.length; j++) {
      const b = weeklyRanges(valid[j]);
      const overlaps = a.some(([s1, e1]) => b.some(([s2, e2]) => s1 < e2 && s2 < e1));
      if (overlaps) {
        return `Rate windows "${valid[i].name || i + 1}" and "${valid[j].name || j + 1}" overlap`;
      }
    }
  }
  return null;
};

// Payload shape sent to the API: no _id, only lines that pick an override PLU for a line still on the tariff.
export const sanitizeRateWindows = (windows, basePlus) =>
  _.map(windows || [], (rateWindow) => ({
    ..._.omit(rateWindow, ['_id', 'id']),
    name: _.trim(rateWindow.name),
    days: _.sortBy(_.uniq(_.map(rateWindow.days, Number))),
    lines: _.map(
      _.filter(rateWindow.lines, (line) => line && line.basePlu && line.plu && (!basePlus || _.includes(basePlus, line.basePlu))),
      (line) => {
        const clean = _.omit(line, ['_id', 'id']);
        // typed inputs give strings; the API wants a number
        if (!_.isNil(clean.unitPrice) && clean.unitPrice !== '') clean.unitPrice = Number(clean.unitPrice);
        return clean;
      }
    ),
  }));

// ---- Dynamic timing (ChargeZone-style setup) ----
// The form keeps one "slot" row per window; days come from the timing type unless it is custom.
// UAE weekend is Saturday + Sunday.
export const RATE_TIMING_TYPES = [
  { label: 'None (flat tariff)', value: 'none' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekdays (Mon - Fri)', value: 'weekdays' },
  { label: 'Weekends (Sat, Sun)', value: 'weekends' },
  { label: 'Custom', value: 'custom' },
];

export const TIMING_TYPE_DAYS = {
  daily: ALL_DAYS,
  weekdays: [1, 2, 3, 4, 5],
  weekends: [6, 0],
};

export const rateTimingTypeLabel = (timingType) =>
  _.get({ none: 'None', daily: 'Daily', weekdays: 'Weekdays', weekends: 'Weekends', custom: 'Custom' }, timingType, timingType || '');

const sameDays = (days, expected) => _.isEqual(_.sortBy(_.uniq(_.map(days, Number))), _.sortBy(expected));

// Edit mode fallback when a tariff has rateWindows but no rateTimingType
export const inferRateTimingType = (rateWindows) => {
  if (_.isEmpty(rateWindows)) return 'none';
  const match = _.find(['daily', 'weekdays', 'weekends'], (timingType) =>
    _.every(rateWindows, (rw) => sameDays(rw.days, TIMING_TYPE_DAYS[timingType]))
  );
  return match || 'custom';
};

export const slotDays = (timingType, slot) => (timingType === 'custom' ? _.get(slot, 'days') || [] : TIMING_TYPE_DAYS[timingType] || []);

export const slotName = (slot) => `${rateWindowTypeLabel(_.get(slot, 'type'))} ${_.get(slot, 'from', '')}–${_.get(slot, 'to', '')}`.trim();

// The energy (kWh) line's window PLU is mandatory on every slot
export const isEnergyLine = (line) => /kwh/i.test(_.get(line, 'unit') || '');

export const distinctBaseLines = (tariffs) =>
  _.uniqBy(
    _.filter(tariffs, (line) => line && line.plu),
    'plu'
  );

// Slots expanded to the rateWindows shape (days resolved + generated name), before sanitising
export const expandSlots = (timingType, slots) => _.map(slots, (slot) => ({ ...slot, name: slotName(slot), days: slotDays(timingType, slot) }));

// Final payload value for `rateWindows`
export const buildRateWindowsPayload = (values) => {
  if (values.version !== 'v3' || !values.rateTimingType || values.rateTimingType === 'none') return [];
  return sanitizeRateWindows(expandSlots(values.rateTimingType, values.rateWindows), _.map(values.tariffs, 'plu'));
};
